import { WebGPUCanvas, Earth, Moon, Camera, PostProcessing } from './components/webgpu/webgpuExports'
import { cameraTransitionState } from './components/camera/Camera'
import { RendererStatsTracker, RendererStatsUI } from './components/ui/debug/RendererStats'
import { HUDFrame } from './components/ui/HUDFrame'
import { Logo } from './components/ui/Logo'
import { Subheading, parseHighlights } from './components/ui/Subheading'
import { ScreenOverlay } from './components/ui/ScreenOverlay'
import DSL_Cards from './components/ui/DSL_Cards'
import { DSL_ScrollBar } from './components/ui/DSL_ScrollBar'
import DSL_Window from './components/ui/DSL_Window'
import DSL_SmallWindow from './components/ui/DSL_SmallWindow'
import { DidYouKnow } from './components/ui/DidYouKnow'
import { CyclingText } from './components/ui/CyclingText'
import { SatelliteInfoPanel } from './components/ui/SatelliteInfoPanel'
import starcadeGames from './data/starcadeGames.json'
import portfolioProjects from './data/portfolioProjects.json'
import { useNavigation } from './components/ui/NavigationManager'
import { Lighting } from './components/lighting/Lighting'
import { Starfield } from './components/starfield/Starfield'
import { SatelliteOrbit } from './components/earth/SatelliteOrbit'
import { OrbitMarker, StaticMarker, EarthSurfaceMarker, MoonSurfaceMarker, SatelliteHotspot } from './components/ui/hotspots/Hotspots2D'
import { Stats, Environment, useTexture } from '@react-three/drei'
import { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect, memo } from 'react'
import { useControls } from 'leva'
import { CustomLoader } from './components/CustomLoader'
import { useFrame } from '@react-three/fiber'
import lodConfig from './components/earth/config/lodConfig.json'
import navigationCameraConfig from './components/camera/config/navigationCameraConfig.json'
import postProcessingConfig from './components/webgpu/config/postProcessingConfig.json'
import { getDeviceInfo, getMediaQueryKey } from './utils/deviceDetection'
import { TrajectoryEditor, TrajectoryEditorUI } from './components/rocket/TrajectoryEditor'
import { LocationProvider, Location, useLocation } from './components/locations'
import { Instances as GatewayInstances, Model as GatewayModel } from './components/models/Gateway'
import { DslSatInstances, DslSat } from './components/models/dsl_sat.jsx'
import { IrusModel } from './components/models/iris.jsx'
import { Prospero } from './components/models/prospero.jsx'
import { MeridianModel } from './components/models/meridian.jsx'
import { LocationSpinController } from './components/locations/LocationSpinController'
import { DotGridBackground } from './components/ui/DotGridBackground'
import { ArtemisOnPath } from './components/models/ArtemisOnPath'
import locationsData from './data/locations.json'
import { ArtemisModeProvider, useArtemisMode } from './components/artemis/ArtemisModeContext'
import { PasswordGate } from './components/ui/PasswordGate'
import { ArtemisChaseCamera } from './components/camera/ArtemisChaseCamera'
import { ArtemisChaseCameraView } from './components/camera/ArtemisChaseCameraView'
import { MobilePortraitLaunchCamera } from './components/camera/MobilePortraitLaunchCamera'
import { MissionStageMarker } from './components/artemis/ArtemisMissionStages'
import { MissionStagesDropdown } from './components/ui/MissionStagesDropdown'
import missionStagesConfig from './components/artemis/artemisMissionStages.json'
import missionStageTooltips from './components/artemis/artemisMissionStageTooltips.json'
import { QRCodeSVG } from 'qrcode.react'
import { FaLinkedin } from 'react-icons/fa'
import { AmbientMusic } from './components/ui/AmbientMusic'
import { playSound, setMuted } from './utils/soundFX'
import { trackEvent } from './utils/analytics'
import { EquirectangularReflectionMapping } from 'three'

// SunRotation: updates sunPositionRef imperatively every frame — no React state, no re-renders.
// When targetRotation is provided the sun animates to that angle over the same duration as the
// camera transition (read from cameraTransitionState.duration at the moment the target changes),
// using the same easeInOutCubic curve so sun and camera move in lockstep.
// When targetRotation is null the sun orbits continuously.
function easeInOutCubicSun(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function SunRotation({ rotationSpeed, sunDistance, sunPositionRef, targetRotation = null }) {
  const angleRef = useRef(0)
  const prevTargetRef = useRef(targetRotation)
  // Active tween: { startAngle, deltaAngle, elapsed, duration }
  // deltaAngle is the shortest-arc difference so the sun never spins the long way round.
  const sunTweenRef = useRef(null)

  useLayoutEffect(() => {
    if (targetRotation !== null && targetRotation !== prevTargetRef.current) {
      const start = angleRef.current
      // Compute shortest-arc delta so continuous orbit accumulation doesn't cause over-spin
      let delta = ((targetRotation - start) % (Math.PI * 2))
      if (delta > Math.PI)  delta -= Math.PI * 2
      if (delta < -Math.PI) delta += Math.PI * 2
      sunTweenRef.current = {
        startAngle: start,
        deltaAngle: delta,
        elapsed: 0,
        duration: cameraTransitionState.duration,  // match camera transition exactly
      }
    }
    prevTargetRef.current = targetRotation
  }, [targetRotation])

  useFrame((_, delta) => {
    if (sunTweenRef.current) {
      const tw = sunTweenRef.current
      tw.elapsed = Math.min(tw.elapsed + delta, tw.duration)
      const t = easeInOutCubicSun(tw.elapsed / tw.duration)
      angleRef.current = tw.startAngle + tw.deltaAngle * t
      if (tw.elapsed >= tw.duration) {
        angleRef.current = tw.startAngle + tw.deltaAngle   // snap to exact end value
        sunTweenRef.current = null
      }
    } else if (targetRotation === null) {
      angleRef.current += rotationSpeed * delta
    }

    const a = angleRef.current % (Math.PI * 2)
    sunPositionRef.current[0] = Math.cos(a) * sunDistance * 0.2
    sunPositionRef.current[1] = 0
    sunPositionRef.current[2] = Math.sin(a) * sunDistance * 0.2
  })
  return null
}

// SatelliteScaleAnimator: drives scale on each satellite group ref imperatively
// every frame — no React state, no re-renders.
// targetScalesRef is an array of { ref, target } where target is the desired
// uniform scale value (0 = hidden, >0 = visible at that scale).
// Each frame each model's scale lerps toward its target so models scale in/out
// smoothly instead of popping.
const SAT_SCALE_SPEED = 8  // higher = faster scale transition
function SatelliteScaleAnimator({ targetScalesRef }) {
  useFrame((_, delta) => {
    const entries = targetScalesRef.current
    if (!entries) return
    for (const { ref, target } of entries) {
      if (!ref.current) continue
      const s = ref.current.scale
      const cur = s.x
      if (Math.abs(cur - target) < 0.0001) {
        if (cur !== target) s.setScalar(target)
        continue
      }
      const next = cur + (target - cur) * Math.min(delta * SAT_SCALE_SPEED, 1)
      s.setScalar(next)
    }
  })
  return null
}

// SpaceEnvironment: always mounted so the texture is preloaded at startup and
// location transitions never trigger a Suspense boundary.
//
// PMREMGenerator warm-up: Three.js compiles the IBL cube-map shader the first
// time environmentIntensity goes above 0. To avoid that GPU stall on the first
// location transition we keep a near-zero base intensity (0.0001) at all times —
// visually identical to off but keeps the shader live. When a location with
// hdri:true is focused the intensity rises to ENV_ACTIVE_VALUE.
const SPACE_ENV_PATH    = '/hdri/earth.webp'
const ENV_ACTIVE_VALUE  = 0.15
const ENV_WARMUP_VALUE  = 0.0001   // near-zero: pre-warms PMREMGenerator, invisible
useTexture.preload(SPACE_ENV_PATH)

function SpaceEnvironment({ active = false, intensity = ENV_ACTIVE_VALUE }) {
  const texture   = useTexture(SPACE_ENV_PATH)
  texture.mapping = EquirectangularReflectionMapping

  return <Environment map={texture} background={false} environmentIntensity={active ? intensity : ENV_WARMUP_VALUE} />
}

function MoonOrbit({
  earthPosition, 
  orbitRadius, 
  orbitSpeed, 
  onPositionUpdate,
  isOrbiting = false,
  orbitRotation =.75, // Rotation offset in radians to control starting position
  children 
}) {
  const angleRef = useRef(orbitRotation)
  const lastUpdateTimeRef = useRef(0)
  const UPDATE_THROTTLE = 100 // Only update parent state every 100ms
  
  // Reusable position array to prevent recreation
  const positionRef = useRef([
    earthPosition[0] + Math.cos(orbitRotation) * orbitRadius,
    earthPosition[1],
    earthPosition[2] + Math.sin(orbitRotation) * orbitRadius
  ])

  useFrame((state, delta) => {
    if (!isOrbiting) return
    
    // Update angle continuously but don't trigger state update
    angleRef.current += orbitSpeed * delta
    
    // Throttle parent state updates to reduce re-renders
    const now = state.clock.getElapsedTime() * 1000
    if (now - lastUpdateTimeRef.current > UPDATE_THROTTLE) {
      lastUpdateTimeRef.current = now
      const angle = -angleRef.current + orbitRotation
      // Reuse position array instead of creating new one
      positionRef.current[0] = earthPosition[0] + Math.cos(angle) * orbitRadius
      positionRef.current[1] = earthPosition[1]
      positionRef.current[2] = earthPosition[2] + Math.sin(angle) * orbitRadius
      onPositionUpdate(positionRef.current)
    }
  })

  // Calculate Moon's orbital position for rendering (static — isOrbiting is always false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const moonPosition = useMemo(() => [
    earthPosition[0] + Math.cos(orbitRotation) * orbitRadius,
    earthPosition[1],
    earthPosition[2] + Math.sin(orbitRotation) * orbitRadius
  ], []) // empty — values never change, computed once at mount
  
  return <>{children(moonPosition)}</>
}


function SceneContent() {
  const { currentLocation } = useLocation()
  const { isArtemisModeActive } = useArtemisMode()

  // 4K detection for QR code sizing
  const [is4K, setIs4K] = useState(() => window.matchMedia('(min-width: 2560px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 2560px)')
    const handler = (e) => setIs4K(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  
  // Timeout tracking for cleanup on unmount
  const timeoutsRef = useRef([])
  
  // Helper to create tracked timeouts that auto-cleanup
  const createTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      callback()
      // Remove from tracking array after execution
      timeoutsRef.current = timeoutsRef.current.filter(id => id !== timeoutId)
    }, delay)
    timeoutsRef.current.push(timeoutId)
    return timeoutId
  }, [])
  
  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId))
      timeoutsRef.current = []
    }
  }, [])
  
  // Memoize Gateway position/scale so .find() doesn't create new references every render
  const gatewaySpacestation = useMemo(
    () => locationsData.locations.find(loc => loc.id === 'spacestation'),
    []
  )
  const launchPlatform = useMemo(
    () => locationsData.locations.find(loc => loc.content?.type === 'launchplatform'),
    []
  )
  // Ordered list of Earth surface launch platform locations that can be cycled through via the panel arrow.
  // Order is determined by their position in locations.json. To add/remove/reorder, edit that file only.
  const earthLocations = useMemo(
    () => locationsData.locations.filter(loc => loc.content?.type === 'launchplatform'),
    []
  )
  const lunarOpsLocation = useMemo(
    () => locationsData.locations.find(loc => loc.id === 'lunarops'),
    []
  )
  const satelliteLocation = useMemo(
    () => locationsData.locations.find(loc => loc.id === 'satellite'),
    []
  )
  const satellitePosition = useMemo(
    () => satelliteLocation?.worldPosition || [-1.06, -0.92, 0.53],
    [satelliteLocation]
  )
  const gatewayPosition = useMemo(
    () => gatewaySpacestation?.content?.modelPosition || [3.5, 0, 3.5],
    [gatewaySpacestation]
  )
  const gatewayScale = gatewaySpacestation?.content?.modelScale ?? 1
  const gatewayRotation = useMemo(
    () => gatewaySpacestation?.content?.modelRotation || [0, 0, 0],
    [gatewaySpacestation]
  )
  const satelliteRotation = useMemo(
    () => satelliteLocation?.content?.modelRotation || [0, 0, 0],
    [satelliteLocation]
  )
  const satelliteModels = useMemo(() => satelliteLocation?.models ?? [], [satelliteLocation])

  // Control points for Artemis trajectory (extracted from TrajectoryEditor)
  const artemisControlPoints = useMemo(() => [
    {
      name: 'Point 1',
      pos: [-0.5990537004352219, 0.7855195297516792, -0.015056705748236254],
      handleIn: [0.17231374545989778, 0.020685094624550964, -0.7698241135063516],
      handleOut: [-0.1366204869874188, -0.016400361407295172, 0.6103619012005782],
      locked: true
    },
    {
      name: 'Point 2',
      pos: [0.19920606923859396, 0.4641273684325985, 0.9631936230747897],
      handleIn: [-0.5400209792508709, 0.16869982411821652, 0.03130916282808059],
      handleOut: [0.5005732592972175, -0.15637655581245974, -0.02902207559502757],
      locked: false
    },
    {
      name: 'Point 3',
      pos: [1.1360250575041544, 0.14628628755888196, 0],
      handleIn: [-0.006225333587299708, 0.06953680113103006, 0.5573671007202332],
      handleOut: [0.007621412455356378, -0.08513096283335884, -0.6823609536846734],
      locked: false
    },
    {
      name: 'Point 4',
      pos: [-0.045605215967133295, 0, -1.1218609913715833],
      handleIn: [0.5490623052680781, 0.001104998585977096, 0],
      handleOut: [-0.5801297251004675, -0.0011675223736335547, 0],
      locked: true
    },
    {
      name: 'Point 5',
      pos: [-1.173700828296186, 0, 0.010998775134302474],
      handleIn: [0.019267985273808907, -0.009710730234103515, -0.6726912436012178],
      handleOut: [-0.01906009558740589, 0.00960595743952098, 0.6654333196568599],
      locked: true
    },
    {
      name: 'Point 6',
      pos: [1.3170889472460063, 0, 1.2632736533722495],
      handleIn: [-1.9990330967394867, 0.02952984854042162, 0.19531135426002022],
      handleOut: [2.924277012049639, -0.043197612584258326, -0.285710378875879],
      locked: true
    },
    {
      name: 'Point 7',
      pos: [1.2789711483813635, 0, -1.356448160914335],
      handleIn: [2.781139651229224, -0.0015982770829863918, 0.14831933456843155],
      handleOut: [-1.8466222815784155, 0.0010612246933642266, -0.0984811344809291],
      locked: true
    },
    {
      name: 'Point 8',
      pos: [-1.2408941292727858, 0, 0.01621809208281144],
      handleIn: [-0.04653962240511139, -0.00827200656078133, -0.8925306548351956],
      handleOut: [0.04062002289606337, 0.007219850066046529, 0.7790053670668001],
      locked: true
    },
    {
      name: 'Point 9',
      pos: [1.7492852394532346, 0, 2.4679249697712318],
      handleIn: [-2.710614835969686, 0, -0.723598180434647],
      handleOut: [1.3467833278764352, 0, 0.35952358577808863],
      locked: true
    },
    {
      name: 'Point 10',
      pos: [4.701569373495156, 0, 3.3659997767833763],
      handleIn: [-0.7723560282918651, 0, -0.6905871785094391],
      handleOut: [0.8322553791688849, 0, 0.7441450225625139],
      locked: true
    },
    {
      name: 'Point 11',
      pos: [3.676694238331191, 0.030746050111627038, 4.450018793131993],
      handleIn: [0.7713003064512374, -0.038065752961593424, 0.7443337562510185],
      handleOut: [-0.6944774711929971, 0.034274338587334724, -0.6701968357087287],
      locked: true
    },
    {
      name: 'Point 12',
      pos: [2.479538541538331, 0.2603853518843079, 1.443347661397742],
      handleIn: [0.2, -0.05617233715136877, 0.7233801520565262],
      handleOut: [-0.21042157031776762, 0.05909935695905046, -0.7610739376621987],
      locked: true
    },
    {
      name: 'Point 13',
      pos: [0.9342015624483669, 0.5980233353682007, -0.8726750112293497],
      handleIn: [0.7835577501344905, -0.07178613322595573, 0.3090824118679104],
      handleOut: [-0.5289469402699052, 0.048459804675736635, -0.20864855975288688],
      locked: true
    },
    {
      name: 'Point 14',
      pos: [-0.5077051140903899, 0.692249269791709, -0.47785596292875454],
      handleIn: [0.21421220640607774, 0, -0.4500981099257835],
      handleOut: [-0.2, 0, 0.420235725570691],
      locked: true
    }
  ], [])
  
  // Check for dev mode from URL query parameter
  const isDevMode = new URLSearchParams(window.location.search).get('dev') === 'true'
  
  // Detect device and orientation — tracked as state so orientation changes
  // (portrait ↔ landscape) trigger a re-render and update UI labels/layout
  const [deviceInfo, setDeviceInfo] = useState(() => getDeviceInfo())
  const isMobile = deviceInfo.isMobile
  
  // Scrollbar refs
  const starcadeScrollRef = useRef(null)
  const portfolioScrollRef = useRef(null)
  const windowScrollRef = useRef(null)
  
  // Get media query key for camera config — tracked as state so orientation
  // changes (portrait ↔ landscape) trigger a re-render and update the camera config
  const [mediaQueryKey, setMediaQueryKey] = useState(() => getMediaQueryKey())
  // When mediaQueryKey changes due to orientation, snap the camera instantly (no animation)
  const snapCameraRef = useRef(false)
  useEffect(() => {
    const update = () => {
      const newKey = getMediaQueryKey()
      setMediaQueryKey(prev => {
        if (prev !== newKey) snapCameraRef.current = true
        return newKey
      })
      setDeviceInfo(getDeviceInfo())
    }
    const handleResize = () => update()
    // iOS fires orientationchange before dimensions update — delay to get correct values
    const handleOrientationChange = () => setTimeout(update, 100)
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleOrientationChange)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [])
  
  // Track Earth's rotation for syncing hotspot markers (ref avoids React state lag)
  const earthRotationRef = useRef(0)
  // Stable callback — inline arrow would be recreated every render and could
  // cause Earth to miss one rotation update on unrelated SceneContent re-renders
  const handleRotationUpdate = useCallback((r) => { earthRotationRef.current = r }, [])

  // Ref to Earth's inner spinning group — used by EarthSurfaceMarker to read
  // Earth's world matrix directly (includes tilt + rotation), eliminating desync.
  const earthInnerGroupRef = useRef(null)

  // Moon orbital configuration
  const earthPosition = [0, 0, 0]
  const moonOrbitRadius = 6 
  const earthRotationSpeed = 0.05
  
  // Base offset for all Earth surface coordinates (lat/long adjustments)
  const earthCoordinateOffset = {
    latitude: 0.7,   // Offset in degrees (-90 to 90)
    longitude: 10.5   // Offset in degrees (-180 to 180)
  }
  
  // Sun — ref-based imperative position, no state, no re-renders
  const sunDistance = 250
  const sunRotationSpeed = earthRotationSpeed
  
  // LEVA controls for Moon marker positioning
  const { lunarOpsLat, lunarOpsLon } = useControls('Moon Marker', {
    lunarOpsLat: { value: -39, min: -90, max: 90, step: 0.1, render: (get) => isDevMode && !focusedLocation },
    lunarOpsLon: { value: 145, min: -180, max: 180, step: 0.1, render: (get) => isDevMode && !focusedLocation }
  })
  
  const starfieldRotationSpeed = earthRotationSpeed * 1
  const sunPositionRef = useRef([sunDistance * 0.2, 0, 0]) // initial: angle=0

  // Track preload and scene ready state
  const [preloadProgress, setPreloadProgress] = useState(0)
  const [preloadComplete, setPreloadComplete] = useState(false)
  const [sceneReady, setSceneReady] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('LOADING TEXTURES')

  const handlePreloadProgress = useCallback((progress, complete, currentPhase) => {
    setPreloadProgress(progress)
    setPreloadComplete(complete)
    if (currentPhase) {
      setLoadingMessage(currentPhase)
    }
  }, [])
  
  const handleSceneReady = useCallback(() => {
    setSceneReady(true)
  }, [])
  
  
  const nav = useNavigation()
  const [currentSection, setCurrentSection] = useState(nav.sections.MAIN)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [currentMode, setCurrentMode] = useState(nav.modes.MAIN)
  const [showJoinMission, setShowJoinMission] = useState(false)
  const [isClosingWindow, setIsClosingWindow] = useState(false)
  const [showMissionObjective, setShowMissionObjective] = useState(false)
  const [isClosingMissionObjective, setIsClosingMissionObjective] = useState(false)
  const [showMissionComplete, setShowMissionComplete] = useState(false)
  const [isClosingMissionComplete, setIsClosingMissionComplete] = useState(false)
  const [isClosingStarcade, setIsClosingStarcade] = useState(false)
  const [isClosingArtemis, setIsClosingArtemis] = useState(false)
  const [earthAtArtemisTarget, setEarthAtArtemisTarget] = useState(false)
  const [showTrajectory, setShowTrajectory] = useState(false)
  const [artemisProgress, setArtemisProgress] = useState(0)
  const [artemisComplete, setArtemisComplete] = useState(false)
  const [trajectoryDrawComplete, setTrajectoryDrawComplete] = useState(false)
  const [artemisMissionLaunched, setArtemisMissionLaunched] = useState(false)
  
  // Track Artemis position for mobile portrait launch camera
  // artemisPosition as a ref — updated every frame by ArtemisOnPath, never triggers re-render
  const artemisPositionRef = useRef([0, 0, 0])
  
  // Follow mode state (desktop/tablet toggle)
  const [followModeActive, setFollowModeActive] = useState(false)

  // Focused location state — set to a location data object when a location hotspot is clicked, null otherwise
  const [focusedLocation, setFocusedLocation] = useState(null)
  const [focusedSatelliteIndex, setFocusedSatelliteIndex] = useState(0)
  const [activeSatHotspot, setActiveSatHotspot] = useState(null)
  const [gatewayHotspotActive, setGatewayHotspotActive] = useState(false)
  const [munPanelActive, setMunPanelActive] = useState(false)
  const [meridianPanelActive, setMeridianPanelActive] = useState(false)
  const locationPanelScrollRef = useRef(null)
  const [locationPanelScrolled, setLocationPanelScrolled] = useState(false)
  const [locationPanelHasOverflow, setLocationPanelHasOverflow] = useState(false)
  const locationModelRef = useRef()
  const satelliteModelRef = useRef()
  const irusModelRef = useRef()
  const prosperoModelRef = useRef()
  // Tracks the previously-active satellite index while a camera transition is in flight.
  // Both models stay visible until the transition ends, then the old one is hidden.
  const prevSatelliteIndexRef = useRef(null)

  const activeSatModel = useMemo(
    () => satelliteModels[focusedSatelliteIndex] ?? satelliteModels[0],
    [satelliteModels, focusedSatelliteIndex]
  )

  // Shadow target ref — points the directional light's shadow frustum at the focused model.
  // Updated whenever focusedLocation changes; read imperatively by Lighting each frame.
  const shadowTargetRef = useRef(null)
  useEffect(() => {
    if (!focusedLocation) {
      shadowTargetRef.current = null
      return
    }
    if (focusedLocation.id === 'spacestation') {
      const pos = focusedLocation.content?.modelPosition || [3.5, 0, 3.5]
      shadowTargetRef.current = pos
    } else if (focusedLocation.id === 'satellite') {
      const base = focusedLocation.worldPosition || [-1.06, -0.92, 0.53]
      const offset = activeSatModel?.positionOffset || [0, 0, 0]
      shadowTargetRef.current = [base[0] + offset[0], base[1] + offset[1], base[2] + offset[2]]
    } else {
      shadowTargetRef.current = focusedLocation.worldPosition || null
    }
  }, [focusedLocation, activeSatModel])

  // Deferred shadow frustum — the frustum parameters change whenever focusedLocation
  // changes, but updating them in the same frame as HDRI / model visibility / camera
  // changes stacks too much GPU work onto a single frame.
  // We hold the "committed" values in state and only update them one macrotask later
  // (via setTimeout 0) so the frustum rebuild is pushed to a subsequent frame.
  const [deferredShadow, setDeferredShadow] = useState({
    frustumSize: 5,
    near: 40,
    far: 60,
  })
  useEffect(() => {
    const next = {
      frustumSize: focusedLocation?.shadow?.frustumSize ?? 5,
      near:        focusedLocation?.shadow?.near        ?? 40,
      far:         focusedLocation?.shadow?.far         ?? 60,
    }
    // Small delay to push the frustum update off the transition frame
    const id = setTimeout(() => setDeferredShadow(next), 0)
    return () => clearTimeout(id)
  }, [focusedLocation])

  // Deferred HDRI activation — satellite and spacestation both have hdri:true.
  // The PMREMGenerator IBL re-evaluation (first frame that environmentIntensity
  // leaves near-zero) is expensive. Deferring it by one macrotask staggers it
  // away from the camera config change, CRT glitch, and shadow-target update
  // that all land on the same frame as focusedLocation changing.
  const [deferredHdri, setDeferredHdri] = useState(0)
  useEffect(() => {
    if (!focusedLocation?.hdri) {
      const id = setTimeout(() => setDeferredHdri(0), 0)
      return () => clearTimeout(id)
    }
    // When in satellite location, each model can override the intensity
    const intensity = (focusedLocation.id === 'satellite' && activeSatModel?.hdriIntensity != null)
      ? activeSatModel.hdriIntensity
      : (focusedLocation.hdriIntensity ?? ENV_ACTIVE_VALUE)
    const id = setTimeout(() => setDeferredHdri(intensity), 0)
    return () => clearTimeout(id)
  }, [focusedLocation, activeSatModel])

  // Satellite visibility — immediate (not deferred).
  // deferredShadow resets one macrotask after focusedLocation changes. Hiding the
  // model in the same render as focusedLocation → null guarantees the model is gone
  // before the shadow frustum resets, so there is never a frame with shadow but no model.
  const satModelVisible = focusedLocation?.id === 'satellite'

  // Navigate to/from a location with the HUD glitch transition
  // targetScalesRef holds the desired scale for each satellite model.
  // SatelliteScaleAnimator (inside the canvas) lerps toward these each frame.
  const targetScalesRef = useRef([
    { ref: satelliteModelRef, target: 0 },
    { ref: irusModelRef,      target: 0 },
    { ref: prosperoModelRef,  target: 0 },
  ])

  // Scale all satellites to zero immediately; after the camera zoom finishes (600ms),
  // scale up only the currently-focused satellite to its configured scale.
  useEffect(() => {
    // Dismiss any open hotspot info panel whenever the satellite or location changes.
    setActiveSatHotspot(null)
    setGatewayHotspotActive(false)
    setMunPanelActive(false)
    setMeridianPanelActive(false)

    // Immediately collapse all models to zero.
    targetScalesRef.current.forEach(e => { e.target = 0 })

    if (focusedLocation?.id !== 'satellite') return

    const fullScales = [1, 1, 1]

    const id = setTimeout(() => {
      targetScalesRef.current.forEach((e, i) => {
        e.target = i === focusedSatelliteIndex ? fullScales[i] : 0
      })
    }, 600)

    return () => clearTimeout(id)
  }, [focusedLocation, focusedSatelliteIndex, satelliteModels])

  const handleFocusLocation = useCallback((location) => {
    // Collapse all satellite models to zero immediately — the useEffect above will
    // scale up the correct one after the camera zoom completes (600ms).
    targetScalesRef.current.forEach(e => { e.target = 0 })
    // Set HDRI intensity synchronously so SpaceEnvironment renders at the correct
    // intensity on the same commit that makes the models visible — never a frame without it.
    const enteringSatellite = location?.id === 'satellite'
    if (enteringSatellite) {
      const intensity = activeSatModel?.hdriIntensity ?? location.hdriIntensity ?? ENV_ACTIVE_VALUE
      setDeferredHdri(intensity)
    }
    // Reset to first satellite model when leaving satellite location
    if (!location || location.id !== 'satellite') {
      setFocusedSatelliteIndex(0)
    }
    // Reset scroll hint so the arrow reappears for each new location
    setLocationPanelScrolled(false)
    setLocationPanelHasOverflow(false)
    setIsTransitioning(true)
    setFocusedLocation(location)
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [createTimeout, activeSatModel])

  // Detect scroll on the location panel content to hide the scroll hint arrow
  useEffect(() => {
    const el = locationPanelScrollRef.current
    if (!el) return
    // Check after DOM has painted whether content actually overflows
    const checkOverflow = () => {
      setLocationPanelHasOverflow(el.scrollHeight > el.clientHeight)
    }
    const raf = requestAnimationFrame(checkOverflow)
    const handleScroll = () => { setLocationPanelScrolled(el.scrollTop > 0) }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      cancelAnimationFrame(raf)
      el.removeEventListener('scroll', handleScroll)
    }
  }, [focusedLocation, focusedSatelliteIndex])

  // Global audio mute state
  const [audioMuted, setAudioMuted] = useState(false)
  const handleToggleMute = useCallback(() => {
    setAudioMuted(prev => {
      const next = !prev
      setMuted(next)
      return next
    })
  }, [])
  
  // Chase camera state
  const [chaseCameraRenderTarget, setChaseCameraRenderTarget] = useState(null)
  const [webgpuRenderer, setWebgpuRenderer] = useState(null)
  const [isDropdownExpanded, setIsDropdownExpanded] = useState(false)
  
  // Mission stage tooltip state
  const [activeTooltip, setActiveTooltip] = useState(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const tooltipTimerRef = useRef(null)
  // Last committed tooltip position — used to threshold updates from useFrame-driven onPositionUpdate
  const lastTooltipPositionRef = useRef({ x: -1, y: -1 })

  // Hotspot blink refs (no position state needed - rendered in 3D with Html)
  const hotspotBlinkRef = useRef(1)
  const spacestationHotspotBlinkRef = useRef(1)
  const lunarOpsHotspotBlinkRef = useRef(1)
  const londonHotspotBlinkRef = useRef(1)
  const artemisBlinkRef = useRef(1)
  const moonObjectiveHotspotBlinkRef = useRef(1)
  
  // Mission stage blink refs (no position state needed - rendered in 3D)
  const missionStageBlinkRefs = useRef({})
  
  // Initialize blink refs for each mission stage
  useEffect(() => {
    missionStagesConfig.missionStages.forEach(stage => {
      if (!missionStageBlinkRefs.current[stage.id]) {
        missionStageBlinkRefs.current[stage.id] = 1
      }
    })
  }, [])
  
  
  // Play CourseDraw_Short when the trajectory starts drawing (earthAtArtemisTarget flips to true), at 1.1x speed
  useEffect(() => {
    if (earthAtArtemisTarget) {
      playSound('CourseDraw_Short', 0.8, 1.33)
    }
  }, [earthAtArtemisTarget])

  // Track which mission stages have already triggered a DataBeep so we only play once per stage per mission
  const reachedStagesSoundedRef = useRef(new Set())

  // Reset the sounded set whenever the mission resets
  useEffect(() => {
    if (!artemisMissionLaunched) {
      reachedStagesSoundedRef.current.clear()
    }
  }, [artemisMissionLaunched])

  // Dismiss location focus when leaving MAIN mode
  useEffect(() => {
    if (currentMode !== nav.modes.MAIN) {
      setFocusedLocation(null)
    }
  }, [currentMode, nav.modes.MAIN])

  // Play DataBeep each time a mission stage point turns white (reached)
  useEffect(() => {
    if (!artemisMissionLaunched) return
    missionStagesConfig.missionStages.forEach(stage => {
      if (
        artemisProgress >= stage.trajectoryPercent &&
        !reachedStagesSoundedRef.current.has(stage.id)
      ) {
        reachedStagesSoundedRef.current.add(stage.id)
        playSound('DataBeep')
      }
    })
  }, [artemisProgress, artemisMissionLaunched])

  // Blinking animation for hotspots - uses refs to avoid state updates on every frame
  const rafIdRef = useRef(null)
  
  useEffect(() => {
    if (currentMode !== nav.modes.MAIN && currentMode !== nav.modes.ARTEMIS) return
    
    const animate = () => {
      const time = Date.now() * 0.001
      
      // Update refs every frame (cheap, no re-render)
      if (currentMode === nav.modes.MAIN) {
        // Satellite hotspot - blinkSpeed = 2.0
        const blinkValue1 = Math.sin(time * 2.0) * 0.5 + 0.5
        hotspotBlinkRef.current = blinkValue1 * 0.8 + 0.2
        
        // Space station hotspot - blinkSpeed = 1.5 (slower)
        const blinkValue2 = Math.sin(time * 1.5) * 0.5 + 0.5
        spacestationHotspotBlinkRef.current = blinkValue2 * 0.8 + 0.2
        
        // Lunar Ops hotspot - blinkSpeed = 1.8
        const blinkValue3 = Math.sin(time * 1.8) * 0.5 + 0.5
        lunarOpsHotspotBlinkRef.current = blinkValue3 * 0.8 + 0.2
        
        // London hotspot - blinkSpeed = 2.2
        const blinkValue4 = Math.sin(time * 2.2) * 0.5 + 0.5
        londonHotspotBlinkRef.current = blinkValue4 * 0.8 + 0.2
      }
      
      // Artemis brackets - hard blink on/off (1 second cycle)
      if (currentMode === nav.modes.ARTEMIS) {
        artemisBlinkRef.current = Math.floor((time * 1.0) % 2) // 0 or 1 (off or on)
        
        // Moon objective hotspot - blinkSpeed = 1.6
        const blinkValue5 = Math.sin(time * 1.6) * 0.5 + 0.5
        moonObjectiveHotspotBlinkRef.current = blinkValue5 * 0.8 + 0.2
      }
      
      rafIdRef.current = requestAnimationFrame(animate)
    }
    
    rafIdRef.current = requestAnimationFrame(animate)
    
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
    }
  }, [currentMode, nav.modes.MAIN, nav.modes.ARTEMIS])

  // Prevent body scroll when modal is open
  useEffect(() => {
    // Store original styles for proper restoration
    const originalBodyOverflow = document.body.style.overflow
    const originalBodyTouchAction = document.body.style.touchAction
    
    if (showJoinMission) {
      // Lock scroll on body
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
      
      // Prevent scroll on the canvas container
      const canvas = document.querySelector('canvas')
      const originalCanvasTouchAction = canvas?.parentElement?.style.touchAction || ''
      
      if (canvas?.parentElement) {
        canvas.parentElement.style.touchAction = 'none'
      }
      
      // Cleanup function restores original values
      return () => {
        document.body.style.overflow = originalBodyOverflow
        document.body.style.touchAction = originalBodyTouchAction
        if (canvas?.parentElement) {
          canvas.parentElement.style.touchAction = originalCanvasTouchAction
        }
      }
    } else {
      // Restore scroll when modal closes
      document.body.style.overflow = originalBodyOverflow
      document.body.style.touchAction = originalBodyTouchAction
    }
    
    // Cleanup on unmount - restore to original state
    return () => {
      document.body.style.overflow = originalBodyOverflow
      document.body.style.touchAction = originalBodyTouchAction
    }
  }, [showJoinMission])

  // Handle window open/close with flickering
  const handleOpenWindow = useCallback(() => {
    const section = currentMode !== nav.modes.MAIN
      ? currentMode
      : focusedLocation
        ? focusedLocation.id
        : 'home'
    trackEvent(`${section}_join_mission_open`)
    setIsTransitioning(true)
    setShowJoinMission(true)
    setIsClosingWindow(false)
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [createTimeout, currentMode, focusedLocation, nav.modes.MAIN])

  const handleCloseWindow = useCallback(() => {
    playSound('JoinOurMission_Close')
    const section = currentMode !== nav.modes.MAIN
      ? currentMode
      : focusedLocation
        ? focusedLocation.id
        : 'home'
    trackEvent(`${section}_join_mission_close`)
    setIsTransitioning(true)
    setIsClosingWindow(true)
    createTimeout(() => {
      setShowJoinMission(false)
      setIsClosingWindow(false)
      setIsTransitioning(false)
    }, 400)
  }, [createTimeout, currentMode, focusedLocation, nav.modes.MAIN])

  const handleOpenIframe = useCallback((url) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  const handleOpenMissionObjective = useCallback(() => {
    playSound('MissionObjective_Open')
    trackEvent('artemis_mission_objective_open')
    setIsTransitioning(true)
    setShowMissionObjective(true)
    setIsClosingMissionObjective(false)
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [createTimeout])

  const handleCloseMissionObjective = useCallback(() => {
    playSound('MissionObjective_Close')
    trackEvent('artemis_mission_objective_close')
    setIsTransitioning(true)
    setIsClosingMissionObjective(true)
    createTimeout(() => {
      setShowMissionObjective(false)
      setIsClosingMissionObjective(false)
      setIsTransitioning(false)
   }, 400)
  }, [createTimeout])

  const handleOpenMissionComplete = useCallback(() => {
    trackEvent('artemis_mission_complete')
    setIsTransitioning(true)
    setShowMissionComplete(true)
    setIsClosingMissionComplete(false)
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [createTimeout])

  const handleCloseMissionComplete = useCallback(() => {
    trackEvent('artemis_mission_complete_close')
    setIsTransitioning(true)
    setIsClosingMissionComplete(true)
    createTimeout(() => {
      setShowMissionComplete(false)
      setIsClosingMissionComplete(false)
      setIsTransitioning(false)
    }, 400)
  }, [createTimeout])

  const handleCloseStarcade = useCallback(() => {
    setIsTransitioning(true)
    setIsClosingStarcade(true)
    createTimeout(() => {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
      setIsClosingStarcade(false)
      setIsTransitioning(false)
    }, 400)
  }, [nav, createTimeout])

  const [isClosingPortfolio, setIsClosingPortfolio] = useState(false)

  const handleClosePortfolio = useCallback(() => {
    setIsTransitioning(true)
    setIsClosingPortfolio(true)
    createTimeout(() => {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
      setIsClosingPortfolio(false)
      setIsTransitioning(false)
    }, 400)
  }, [nav, createTimeout])

  const handleCloseArtemis = useCallback(() => {
    setIsTransitioning(true)
    setIsClosingArtemis(true)
    createTimeout(() => {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
      setIsClosingArtemis(false)
      setIsTransitioning(false)
    }, 400)
  }, [nav, createTimeout])

  const handleArtemisClick = useCallback(() => {
    if (currentMode === nav.modes.ARTEMIS) {
      // Trigger trajectory erase animation but change mode immediately
      if (typeof window.trajectoryErase === 'function') {
        window.trajectoryErase()
      }
      
      // Start camera transition immediately - stop mission tracking right away
      setIsTransitioning(true)
      setArtemisMissionLaunched(false) // Stop mobile portrait camera immediately
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
      
      // Keep trajectory visible for erase animation, then hide everything
      createTimeout(() => {
        setShowTrajectory(false)
        setEarthAtArtemisTarget(false)
        setArtemisProgress(0)
        setArtemisComplete(false)
        setTrajectoryDrawComplete(false)
      }, 1000)
      
      // Remove transition state after camera animation
      createTimeout(() => {
        setIsTransitioning(false)
      }, 600)
    } else {
      // Navigate to artemis from any mode
      trackEvent('artemis_enter')
      setIsTransitioning(true)
      setCurrentSection(nav.sections.ARTEMIS)
      setCurrentMode(nav.modes.ARTEMIS)
      setEarthAtArtemisTarget(false)
      setShowTrajectory(true)
      setArtemisProgress(0)
      setArtemisComplete(false)
      setTrajectoryDrawComplete(false)
      setArtemisMissionLaunched(false)
      
      createTimeout(() => {
        setIsTransitioning(false)
      }, 600)
    }
  }, [nav, currentMode, createTimeout])
  
  const handleArtemisReset = useCallback(() => {
    // Set artemisComplete false FIRST so the useEffect that watches
    // artemisComplete + showMissionComplete doesn't re-open the window
    // while the closing animation is still running.
    setArtemisComplete(false)
    
    // Close mission complete window if open
    if (showMissionComplete) {
      handleCloseMissionComplete()
    }
    
    // Step 1: switch to course view immediately so camera transitions smoothly
    setFollowModeActive(false)
    
    // Step 2: reset mission state after a short delay to allow camera to return
    createTimeout(() => {
      if (typeof window.artemisReset === 'function') {
        window.artemisReset()
      }
      setArtemisProgress(0)
      setArtemisMissionLaunched(false)
      // Don't reset trajectoryDrawComplete - keep path visible
    }, 600)
  }, [showMissionComplete, handleCloseMissionComplete, createTimeout])
  
  const handleArtemisMissionLaunch = useCallback(() => {
    setArtemisMissionLaunched(true)
  }, [])
  
  const handleChaseCameraRenderTargetUpdate = useCallback((data) => {
    setChaseCameraRenderTarget(data)
  }, [])
  
  // Handle Artemis position updates for mobile portrait launch camera
  const handleArtemisPositionUpdate = useCallback((position) => {
    // Write directly to ref — no setState, no re-render
    artemisPositionRef.current[0] = position[0]
    artemisPositionRef.current[1] = position[1]
    artemisPositionRef.current[2] = position[2]
  }, [])
  
  // Handle mission stage marker click - show tooltip with 5 second auto-dismiss
  const handleMissionStageClick = useCallback((stageId, stageName, screenX, screenY, event) => {
    // Stop propagation to prevent immediate dismissal
    if (event) {
      event.stopPropagation()
    }
    
    // Clear existing timer if any
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
    }
    
    // Find tooltip data from JSON
    const tooltipData = missionStageTooltips.tooltips.find(t => t.stageId === stageId)
    const displayName = tooltipData ? tooltipData.name : stageName
    const description = tooltipData ? tooltipData.description : null
    
    // Track stage click
    trackEvent(`artemis_${stageId}_clicked`)
    
    // Play DataPoint_Open sound
    playSound('DataPoint_Open')

    // Set active tooltip with position
    setActiveTooltip({ id: stageId, name: displayName, description })
    setTooltipPosition({ x: screenX, y: screenY })
    
    // Set 5 second timer to hide tooltip
    tooltipTimerRef.current = setTimeout(() => {
      playSound('DataPoint_Close')
      setActiveTooltip(null)
      tooltipTimerRef.current = null
    }, 5000)
  }, [])
  
  // Dismiss tooltip when clicking anywhere (but NOT mission complete window)
  const handleDismissTooltip = useCallback(() => {
    if (activeTooltip) {
      playSound('DataPoint_Close')
      setActiveTooltip(null)
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
        tooltipTimerRef.current = null
      }
    }
  }, [activeTooltip])
  
  // Handle continuous position updates from the active marker
  // Guard with 0.5px threshold — this is called from useFrame (60fps) so we must
  // avoid calling setTooltipPosition unless the marker has actually moved
  const handleMarkerPositionUpdate = useCallback((stageId, screenX, screenY) => {
    if (activeTooltip && activeTooltip.id === stageId) {
      const dx = Math.abs(screenX - lastTooltipPositionRef.current.x)
      const dy = Math.abs(screenY - lastTooltipPositionRef.current.y)
      if (dx > 0.5 || dy > 0.5) {
        lastTooltipPositionRef.current.x = screenX
        lastTooltipPositionRef.current.y = screenY
        setTooltipPosition({ x: screenX, y: screenY })
      }
    }
  }, [activeTooltip])
  
  // Show mission complete window when artemis mission finishes
  useEffect(() => {
    if (artemisComplete && !showMissionComplete) {
      // Add small delay for dramatic effect
      createTimeout(() => {
        handleOpenMissionComplete()
      }, 500)
    }
  }, [artemisComplete, showMissionComplete, handleOpenMissionComplete, createTimeout])

  // Cleanup window globals and tooltip timer on unmount
  useEffect(() => {
    return () => {
      // Clean up any window-attached functions when component unmounts
      if (typeof window.trajectoryErase === 'function') {
        delete window.trajectoryErase
      }
      if (typeof window.artemisReset === 'function') {
        delete window.artemisReset
      }
      // Clean up tooltip timer
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
      }
    }
  }, [])

  // lightingPosition is now sunPositionRef — updated imperatively by SunRotation

  const handleLeftButtonClick = useCallback(() => {
    setIsTransitioning(true)
    
    if (currentMode === nav.modes.PORTFOLIO) {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
    } else {
      trackEvent('nav_starcade')
      setCurrentSection(nav.sections.STARCADE)
      setCurrentMode(nav.modes.STARCADE)
    }
    
    // Remove transition state after animation completes
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [nav, currentMode, createTimeout])

  const handleRightButtonClick = useCallback(() => {
    setIsTransitioning(true)
    
    if (currentMode === nav.modes.STARCADE) {
      setCurrentSection(nav.sections.MAIN)
      setCurrentMode(nav.modes.MAIN)
    } else {
      trackEvent('nav_portfolio')
      setCurrentSection(nav.sections.PORTFOLIO)
      setCurrentMode(nav.modes.PORTFOLIO)
    }
    
    // Remove transition state after animation completes
    createTimeout(() => {
      setIsTransitioning(false)
    }, 600)
  }, [nav, currentMode, createTimeout])

  // Only hide loader when BOTH textures are loaded AND scene is ready
  const showLoader = !preloadComplete || !sceneReady
  // Subscribe to iframe state to hide UI/hotspots when iframe is active.


  // Initialize monitoring systems
  useEffect(() => {
    // Development mode info
    if (process.env.NODE_ENV === 'development') {
      // Silent mode - no console output
    }
  }, [])
  
  // Monitor camera mode transitions
  useEffect(() => {
    if (isTransitioning) {
      // Silent transition monitoring
    }
  }, [isTransitioning, currentMode])
  const showOverlay = currentMode === nav.modes.STARCADE || currentMode === nav.modes.PORTFOLIO || showJoinMission
  
  // Determine which buttons to show based on current mode
  // Hide starcade/portfolio buttons when artemis mode is active
  const showLeftButton = !isArtemisModeActive && !focusedLocation && (currentMode === nav.modes.MAIN || currentMode === nav.modes.PORTFOLIO)
  const showRightButton = !isArtemisModeActive && !focusedLocation && (currentMode === nav.modes.MAIN || currentMode === nav.modes.STARCADE)
  
  // Show Artemis button on MAIN mode (to enter) and ARTEMIS mode (to exit)
  const showArtemisButton = currentMode === nav.modes.MAIN && sceneReady && !focusedLocation && !showJoinMission
  
  // Use full post-processing config for all devices (memory leaks fixed)
  const mobileOptimizedPostProcessing = useMemo(() => {
    return postProcessingConfig // Full config for desktop and mobile
  }, [])
  
  // Select camera config based on current mode and media query
  const currentCameraConfig = useMemo(() => 
    navigationCameraConfig.modes[currentMode][mediaQueryKey] || navigationCameraConfig.modes[currentMode].desktopStandard,
    [currentMode, mediaQueryKey]
  )

  // Override camera config when a location is focused (MAIN mode only)
  const activeCameraConfig = useMemo(() => {
    if (focusedLocation && currentMode === nav.modes.MAIN) {
      let cam
      if (focusedLocation.id === 'satellite' && activeSatModel?.camera) {
        // For satellite models, check mobileCamera sibling key on the model
        cam = (mediaQueryKey === 'mobilePortrait' && activeSatModel.mobileCamera)
          ? activeSatModel.mobileCamera
          : activeSatModel.camera
      } else {
        // For regular locations, check mobileCamera sibling key on the location
        cam = (mediaQueryKey === 'mobilePortrait' && focusedLocation.mobileCamera)
          ? focusedLocation.mobileCamera
          : (focusedLocation.camera || null)
      }
      return cam
    }
    return currentCameraConfig
  }, [focusedLocation, currentMode, currentCameraConfig, activeSatModel, mediaQueryKey, nav.modes.MAIN])
  
  return (
    <div onClick={handleDismissTooltip} style={{ width: '100%', height: '100%' }}>
      <HUDFrame
            onLeftButtonClick={handleLeftButtonClick}
            onRightButtonClick={handleRightButtonClick}
            isTransitioning={isTransitioning}
            showLeftButton={showLeftButton}
            showRightButton={showRightButton}
            currentMode={currentMode}
            showJoinMission={showJoinMission}
            onArtemisClick={handleArtemisClick}
            showArtemisButton={showArtemisButton}
            isArtemisModeActive={isArtemisModeActive}
            audioMuted={audioMuted}
            onToggleMute={handleToggleMute}
            showMuteButton={sceneReady && !deviceInfo.isMobile}
          />
          
          <div className="starcade-landscape-message">
            PLEASE ROTATE TO PORTRAIT MODE
          </div>
          <div className="tablet-portrait-message">
            PLEASE ROTATE TO LANDSCAPE MODE
          </div>
          
          <ScreenOverlay isActive={showOverlay} />
          <Logo className={showJoinMission && deviceInfo.isMobile && deviceInfo.isPortrait ? 'hidden-on-mobile-portrait' : ''} />
          {!showJoinMission && (() => {
            const s = focusedLocation ? (nav.locations[focusedLocation.id] ?? { text: focusedLocation.name }) : currentSection
            const isStarcade = currentMode === nav.modes.STARCADE
            return (
              <Subheading
                text={s.text}
                size={s.size}
                imageSrc={isStarcade ? '/logos/STARCADE Logo Light and Orange.svg' : null}
              />
            )
          })()}

          {!showJoinMission && currentMode === nav.modes.STARCADE && (
            <p className="mode-subtitle">"We build branded mini-games because the most effective way to understand a complex mission is to fly it yourself."</p>
          )}


          {!showJoinMission && currentMode === nav.modes.STARCADE && (
            <CyclingText screen="starcade" />
          )}
          {!showJoinMission && currentMode === nav.modes.PORTFOLIO && (
            <CyclingText screen="portfolio" />
          )}
          {!showJoinMission && currentMode === nav.modes.PORTFOLIO && (
            <p className="mode-subtitle">"Our interactive 3D experiences are developed in partnership with the industry's most innovative companies."</p>
          )}

      {(
      <>
      {currentMode === nav.modes.MAIN && focusedLocation && (
        <button
          className="rect_button-alt rect_button-back"
          onClick={() => { playSound('HideCourse'); handleFocusLocation(null); }}
        >
          <span className="rect_button-chevrons">&lt;&lt;&lt;</span>
          <span>BACK</span>
        </button>
      )}


      {currentMode === nav.modes.MAIN && focusedLocation && !showJoinMission && (
        <DSL_SmallWindow
          containerClassName="location-panel"
          side={focusedLocation?.content?.panelSide ?? 'left'}
          chamferScale={0.6}
          noBackdrop={true}
          allowButtonClicks={true}
          hideCloseButton={true}
          isTransitioning={isTransitioning}
          scrollContentRef={locationPanelScrollRef}
        >
          {/* Title row — nav buttons flank title on mobile portrait */}
          <div className="location-panel-title-row">
            {/* Prev button — earth locations */}
            {focusedLocation.id !== 'satellite' && earthLocations.length > 1 && (() => {
              const idx = earthLocations.findIndex(loc => loc.id === focusedLocation.id)
              if (idx === -1) return null
              const prev = earthLocations[(idx - 1 + earthLocations.length) % earthLocations.length]
              return (
                <button className="rect_button-alt location-panel-prev" onClick={() => { playSound('PlotCourse'); handleFocusLocation(prev) }} aria-label={`Previous: ${prev.name}`}>
                  <span className="rect_button-chevrons">&lt;&lt;&lt;</span>
                </button>
              )
            })()}
            {/* Prev button — satellite carousel */}
            {focusedLocation.id === 'satellite' && satelliteModels.length > 1 && (() => {
              const prevIdx = (focusedSatelliteIndex - 1 + satelliteModels.length) % satelliteModels.length
              return (
                <button className="rect_button-alt location-panel-prev" onClick={() => { playSound('PlotCourse'); setFocusedSatelliteIndex(prevIdx) }} aria-label={`Previous: ${satelliteModels[prevIdx].name}`}>
                  <span className="rect_button-chevrons">&lt;&lt;&lt;</span>
                </button>
              )
            })()}
            <h1 className="location-panel-title">
              {parseHighlights((focusedLocation?.id === 'satellite' ? (activeSatModel?.panelName ?? activeSatModel?.name) : null) ?? focusedLocation?.name ?? '')}
            </h1>
            {/* Next button — earth locations */}
            {focusedLocation.id !== 'satellite' && earthLocations.length > 1 && (() => {
              const idx = earthLocations.findIndex(loc => loc.id === focusedLocation.id)
              if (idx === -1) return null
              const next = earthLocations[(idx + 1) % earthLocations.length]
              return (
                <button className="rect_button-alt location-panel-next" onClick={() => { playSound('PlotCourse'); handleFocusLocation(next) }} aria-label={`Next: ${next.name}`}>
                  <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
                </button>
              )
            })()}
            {/* Next button — satellite carousel */}
            {focusedLocation.id === 'satellite' && satelliteModels.length > 1 && (() => {
              const nextIdx = (focusedSatelliteIndex + 1) % satelliteModels.length
              return (
                <button className="rect_button-alt location-panel-next" onClick={() => { playSound('PlotCourse'); setFocusedSatelliteIndex(nextIdx) }} aria-label={`Next: ${satelliteModels[nextIdx].name}`}>
                  <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
                </button>
              )
            })()}
          </div>
          {(() => {
            const desc = focusedLocation?.id === 'satellite'
              ? (activeSatModel?.description ?? focusedLocation?.description)
              : focusedLocation?.description
            if (!desc) return null
            if (Array.isArray(desc)) return desc.map((p, i) => <p key={i} className="location-panel-description">{parseHighlights(p)}</p>)
            return <p className="location-panel-description">{parseHighlights(desc)}</p>
          })()}
          {focusedLocation?.content?.ctaButton && (
            <button
              className="location-panel-cta"
              onClick={() => {
                playSound('PlotCourse')
                trackEvent(`launch-platforms_${focusedLocation.id}`)
                handleOpenIframe(
                  focusedLocation.content.ctaButton.url,
                  focusedLocation.content.ctaButton.label
                )
              }}
            >
              <span>{focusedLocation.content.ctaButton.label}</span>
              <span className="rect_button-chevrons">{focusedLocation.content.ctaButton.chevrons ?? '>>>'}</span>
            </button>
          )}
        </DSL_SmallWindow>
      )}

      {currentMode === nav.modes.MAIN && focusedLocation && !showJoinMission && locationPanelHasOverflow && !locationPanelScrolled && (
        <div className="location-panel-scroll-overlay">
          <div className="location-panel-scroll-gradient" />
          <div className="location-panel-scroll-arrow">&#8964;</div>
        </div>
      )}

      {currentMode === nav.modes.MAIN && focusedLocation && !showJoinMission && locationPanelHasOverflow && locationPanelScrolled && (
        <div className="location-panel-scroll-overlay location-panel-scroll-overlay--top">
          <div className="location-panel-scroll-gradient location-panel-scroll-gradient--top" />
          <div className="location-panel-scroll-arrow location-panel-scroll-arrow--top">&#8963;</div>
        </div>
      )}

      {currentMode === nav.modes.MAIN && !focusedLocation && sceneReady && !showJoinMission && !isArtemisModeActive && (
        <DidYouKnow />
      )}

      {currentMode === nav.modes.MAIN && !focusedLocation && sceneReady && !showJoinMission && !isArtemisModeActive && (
        <CyclingText screen="home" position="bottom-left" />
      )}

      {currentMode === nav.modes.ARTEMIS && !artemisMissionLaunched && trajectoryDrawComplete && !showJoinMission && (
        <DidYouKnow screen="artemis" />
      )}

      <SatelliteInfoPanel
        model={activeSatHotspot !== null ? satelliteModels[activeSatHotspot] : null}
        visible={focusedLocation?.id === 'satellite' && activeSatHotspot !== null && !showJoinMission}
        offset={focusedLocation?.infoPanelOffset}
        onClose={() => setActiveSatHotspot(null)}
      />

      <SatelliteInfoPanel
        model={gatewayHotspotActive ? gatewaySpacestation : null}
        visible={focusedLocation?.id === 'spacestation' && gatewayHotspotActive && !showJoinMission}
        offset={gatewaySpacestation?.infoPanelOffset}
        position={gatewaySpacestation?.infoPanelPosition}
        autoCloseMs={60_000}
        onClose={() => setGatewayHotspotActive(false)}
      />

      <SatelliteInfoPanel
        model={munPanelActive ? {
          infoPanelName: 'Think you have what it takes to navigate the lunar surface?',
          info: [
            'Take the wheel in MÜN - our high-speed time trial to collect lunar samples and map out key lunar resources.',
            'We believe mini-games are the ultimate tool for engagement; they turn passive viewers into active participants, improving product and mission understanding with fun and memorable experiences.',
          ],
        } : null}
        visible={focusedLocation?.id === 'lunarops' && munPanelActive && !showJoinMission}
        position="top-left"
        offset={['tabletPortrait', 'tabletLandscape'].includes(mediaQueryKey)
          ? lunarOpsLocation?.munPanelOffsetTablet
          : lunarOpsLocation?.munPanelOffset}
        thumbnail={!['tabletPortrait', 'tabletLandscape'].includes(mediaQueryKey) ? "/card_images/MUN_Thumbnail_Wtext.webp" : undefined}
        ctaLabel="PLAY NOW"
        onCta={() => { trackEvent('lunarops_mun'); handleOpenIframe('https://mun.darkstarlabs.io/', 'Explore the Mün') }}
        onClose={() => setMunPanelActive(false)}
      />

      <SatelliteInfoPanel
        model={meridianPanelActive ? {
          infoPanelName: 'ECONOMIC TRANSFER VEHICLE',
          info: [
            "Dr. Sam Richards at Meridian Space Command is developing the 'Economical Transfer Vehicle' (ETV), which uses Alpha Impulsion's \"self-eating\" technology. The ETV is currently in Preliminary Design Review (PDR) funded by the UK Space Agency.",
            'The ETV would hitch a ride into LEO and then "eat" its own fuel tank to get to the moon. Lowering costs for science beyond earth orbit.',
          ],
        } : null}
        visible={focusedLocation?.id === 'lunarops' && meridianPanelActive && !showJoinMission}
        position="bottom-left"
        offset={lunarOpsLocation?.meridianPanelOffset}
        ctaLabel="OPEN EXPERIENCE"
        onCta={() => { trackEvent('lunarops_meridian-etv'); handleOpenIframe('https://meridian-space-etv.vercel.app/', "Meridian's ETV") }}
        onClose={() => setMeridianPanelActive(false)}
      />

      {(currentMode === nav.modes.STARCADE || currentMode === nav.modes.PORTFOLIO || (currentMode === nav.modes.MAIN && !isArtemisModeActive && sceneReady)) && !showJoinMission && (
        <button
          className="rect_button-alt rect_button-join-mission"
          onClick={() => {
            playSound('JoinOurMission_Open')
            handleOpenWindow()
          }}
        >
          <span>{(deviceInfo.isPhone || (deviceInfo.isTablet && deviceInfo.isPortrait)) ? 'JOIN' : 'JOIN OUR MISSION'}</span>
          <span className="rect_button-chevrons">[ ]</span>
        </button>
      )}

      {currentMode === nav.modes.MAIN && !showJoinMission && !(showMissionObjective && deviceInfo.isMobile) && isArtemisModeActive && sceneReady && (
        <>
          {/* Join Our Mission button - bottom right */}
          <button 
            className="rect_button-alt rect_button-join-mission" 
            onClick={() => {
              playSound('JoinOurMission_Open')
              handleOpenWindow()
            }}
          >
            <span>{(deviceInfo.isPhone || (deviceInfo.isTablet && deviceInfo.isPortrait)) ? 'JOIN' : 'JOIN OUR MISSION'}</span>
            <span className="rect_button-chevrons">[ ]</span>
          </button>
          
          {/* Plot button with Artemis logo inside - centered */}
          <button 
            className="rect_button rect_button-artemis-plot" 
            onClick={() => {
              playSound('PlotCourse')
              trackEvent('artemis_plot_course')
              if (showMissionObjective) {
                handleCloseMissionObjective()
              }
              handleArtemisClick()
            }}
          >
            <img 
              src="/images/artemis-filled.svg"
              alt="Artemis"
            />
            <span>{['mobilePortrait', 'mobileLandscape', 'tabletPortrait'].includes(mediaQueryKey) ? 'PLOT COURSE' : 'PLOT ARTEMIS II COURSE'}</span>
            <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
          </button>
        </>
      )}
      
      {currentMode === nav.modes.ARTEMIS && !showJoinMission && (
        <>
          {/* Join Our Mission button - bottom right - always visible in Artemis mode */}
          <button 
            className="rect_button-alt rect_button-join-mission" 
            onClick={() => {
              playSound('JoinOurMission_Open')
              if (showMissionObjective) {
                handleCloseMissionObjective()
              }
              handleOpenWindow()
            }}
          >
            <span>{(deviceInfo.isPhone || (deviceInfo.isTablet && deviceInfo.isPortrait)) ? 'JOIN' : 'JOIN OUR MISSION'}</span>
            <span className="rect_button-chevrons">[ ]</span>
          </button>
          
          {!artemisMissionLaunched && trajectoryDrawComplete && (
            <>
              <div className="camera-instruction-text">
                CLICK OR TOUCH AND DRAG TO ROTATE CAMERA
              </div>
          
              
              <button 
                className="rect_button rect_button-artemis-plot" 
                onClick={() => {
                  playSound('LaunchMission')
                  trackEvent('artemis_launch_mission')
                  handleArtemisMissionLaunch()
                }}
              >
                <img 
                  src="/images/artemis-filled.svg"
                  alt="Artemis"
                />
                <span>LAUNCH MISSION</span>
                <span className="rect_button-chevrons">&gt;&gt;&gt;</span>
              </button>
              
              <button 
                className="rect_button-alt rect_button-back" 
                onClick={() => {
                  playSound('HideCourse')
                  trackEvent('artemis_hide_course')
                  handleArtemisClick()
                }}
              >
                <span className="rect_button-chevrons" style={{ transform: 'scaleX(-1)', display: 'inline-block' }}>&gt;&gt;&gt;</span>
                <span>{(deviceInfo.isPhone || (deviceInfo.isTablet && deviceInfo.isPortrait)) ? 'HIDE' : 'HIDE COURSE'}</span>
              </button>
            </>
          )}
          
          {artemisMissionLaunched && (
            <>
              {/* Follow mode toggle button - only on desktop/tablet, NOT on mobile portrait */}
              {mediaQueryKey !== 'mobilePortrait' && (
                <button 
                  className="rect_button rect_button-artemis-plot" 
                  onClick={() => {
                    playSound('TrackVehicle')
                    trackEvent('artemis_track_orion', { follow_mode: !followModeActive })
                    setFollowModeActive(!followModeActive)
                  }}
                >
                  <span className="rect_button-chevrons">{followModeActive ? '<<<' : ''}</span>
                  <span>{followModeActive ? 'COURSE VIEW' : 'TRACK ORION'}</span>
                  <span className="rect_button-chevrons">{followModeActive ? '' : '>>>'}</span>
                </button>
              )}
              
              <button 
                className="rect_button-alt rect_button-back" 
                onClick={() => {
                  playSound('AbandonMission')
                  trackEvent('artemis_abandon_mission', { mission_complete: artemisComplete })
                  handleArtemisReset()
                }}
              >
                <span className="rect_button-chevrons" style={{ transform: 'scaleX(-1)', display: 'inline-block' }}>&gt;&gt;&gt;</span>
                <span>{artemisComplete ? 'RESET' : 'ABORT'}</span>
              </button>
              
              {/* Mission stages - dropdown on mobile portrait, regular list on other devices */}
              {mediaQueryKey === 'mobilePortrait' ? (
                <MissionStagesDropdown
                  missionStages={missionStagesConfig.missionStages}
                  artemisProgress={artemisProgress}
                  mediaQueryKey={mediaQueryKey}
                  onExpandedChange={setIsDropdownExpanded}
                />
              ) : (
                <div className="mission-stages-container">
                  {/* Heading */}
                  <div className="mission-stages-heading">
                    {'<<< MISSION STAGES'}
                  </div>
                  <div className="mission-stages-divider" />
                  
                  {/* Stages list */}
                  {missionStagesConfig.missionStages
                    .filter(stage => stage.visible)
                    .map((stage, index) => {
                      const reached = artemisProgress >= stage.trajectoryPercent
                      const stageNumber = (index + 1).toString().padStart(2, '0')
                      // Use short name on mobile/tablet/desktopStandard, full name on large desktop
                      const isDesktop = mediaQueryKey === 'desktopStandard' || mediaQueryKey === 'desktopLarge' || mediaQueryKey === 'desktop4k'
                      const isLargeDesktop = mediaQueryKey === 'desktopLarge' || mediaQueryKey === 'desktop4k'
                      const displayName = isLargeDesktop ? stage.name : (stage.shortName || stage.name)
                      return (
                        <div
                          key={stage.id}
                          className={`mission-stage-item ${reached ? 'reached' : 'pending'}`}
                        >
                          <span className="mission-stage-number">{stageNumber}</span>
                          <span className={`mission-stage-connector ${reached ? 'reached' : 'pending'}`}></span>
                          <span className="mission-stage-name">{displayName}{reached && isDesktop ? ' /// COMPLETE ///' : ''}</span>
                        </div>
                      )
                    })}
                </div>
              )}
            </>
          )}
        </>
      )}
      
      {currentMode === nav.modes.STARCADE && !showJoinMission && <DSL_Cards section="starcade" data={starcadeGames} isTransitioning={isTransitioning} onClose={handleCloseStarcade} isClosing={isClosingStarcade} scrollContainerRef={starcadeScrollRef} />}
      {currentMode === nav.modes.STARCADE && !showJoinMission && <DSL_ScrollBar scrollContainerRef={starcadeScrollRef} />}
      
      {currentMode === nav.modes.PORTFOLIO && !showJoinMission && <DSL_Cards section="portfolio" data={portfolioProjects} isTransitioning={isTransitioning} onClose={handleClosePortfolio} isClosing={isClosingPortfolio} scrollContainerRef={portfolioScrollRef} />}
      {currentMode === nav.modes.PORTFOLIO && !showJoinMission && <DSL_ScrollBar scrollContainerRef={portfolioScrollRef} />}
      
      {showJoinMission && <DSL_Window onClose={handleCloseWindow} isClosing={isClosingWindow} isTransitioning={isTransitioning} scrollContentRef={windowScrollRef} />}
      {showJoinMission && <DSL_ScrollBar scrollContainerRef={windowScrollRef} />}
      </>
      )}

      {showLoader && (
        <CustomLoader 
          progress={preloadProgress}
          dataInterpolation={(p) => `${loadingMessage} ${Math.floor(p)}%`}
        />
      )}

      {isDevMode && <RendererStatsUI />}
      
      {/* Mute/unmute button — styled via .mute-button in style.css */}
      {sceneReady && (
        <button
          className={`mute-button${audioMuted ? ' muted' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleToggleMute() }}
          title={audioMuted ? 'Unmute' : 'Mute'}
        >
          {audioMuted ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          )}
        </button>
      )}

      {/* Ambient music — starts when scene is ready, fades in over 3 seconds */}
      <AmbientMusic
        src="/music/Artemis_Ambient-Loop.mp3"
        volume={0.4}
        fadeInDuration={3.0}
        enabled={sceneReady}
        muted={audioMuted}
      />


      <WebGPUCanvas 
        onPreloadProgress={handlePreloadProgress} 
        onSceneReady={handleSceneReady}
        onCreated={({ gl }) => setWebgpuRenderer(gl)}
        mediaQueryKey={mediaQueryKey}
      >
        {/* Stats hidden */}
        {false && isDevMode && <Stats className="stats-panel" />}
      {isDevMode && <RendererStatsTracker />}

        <PostProcessing
          enabled={mobileOptimizedPostProcessing.enabled}
          bloomConfig={mobileOptimizedPostProcessing.bloom}
          lensflareConfig={mobileOptimizedPostProcessing.lensflare}
          chromaticAberrationConfig={mobileOptimizedPostProcessing.chromaticAberration}
          crtConfig={mobileOptimizedPostProcessing.crt}
        />
        
        <SunRotation 
          rotationSpeed={sunRotationSpeed} 
          sunDistance={sunDistance} 
          sunPositionRef={sunPositionRef}
          targetRotation={focusedLocation?.sun?.reposition ? (focusedLocation.sun.targetRotation ?? null) : null}
        />
        
        <Lighting
          positionRef={sunPositionRef}
          shadowTargetRef={shadowTargetRef}
          shadowFrustumSize={deferredShadow.frustumSize}
          shadowNear={deferredShadow.near}
          shadowFar={deferredShadow.far}
          ambientIntensity={focusedLocation?.ambient?.intensity ?? 0.1}
          ambientColor={focusedLocation?.ambient?.color ?? '#4db2ff'}
          showShadowHelper={false}
        />

        <SpaceEnvironment active={deferredHdri > 0} intensity={deferredHdri} />
        
        <DotGridBackground visible={!!focusedLocation || currentMode === nav.modes.STARCADE || currentMode === nav.modes.PORTFOLIO} />
        
        <Starfield 
          count={5000}
          radius={1000}
          rotationSpeed={starfieldRotationSpeed}
        />
        
        <Camera 
          config={activeCameraConfig}
          trajectoryEditorMode={currentMode === nav.modes.ARTEMIS}
          mobilePortraitLaunchMode={artemisMissionLaunched && deviceInfo.isPhone && deviceInfo.isPortrait}
          followModeActive={artemisMissionLaunched && followModeActive}
          disableMouseFollow={!!focusedLocation}
          snapToConfig={snapCameraRef}
        />
        
        {/* Mobile portrait launch camera - activates when LAUNCH pressed on mobile portrait OR follow mode toggled */}
        <MobilePortraitLaunchCamera
          isActive={artemisMissionLaunched}
          followModeActive={followModeActive}
          artemisPositionRef={artemisPositionRef}
          cameraOffset={[0.5, 1.5, 0.95]}
          lookAtOffset={[0, -0.1, 0]}
          smoothTime={0.6}
          returnPosition={currentCameraConfig.position}
          returnLookAt={currentCameraConfig.lookAt}
        />
        
        <Earth
          radius={1}
          rotation={[0, 0, -23.5 * Math.PI / 180]}
          autoRotate={true}
          rotationSpeed={earthRotationSpeed}
          textureQuality="auto"
          enableAtmosphere={true}
          position={earthPosition}
          onLoad={() => {}}
          preloadComplete={preloadComplete}
          sunPositionRef={sunPositionRef}
          artemisActive={currentMode === nav.modes.ARTEMIS}
          onArtemisTargetReached={() => setEarthAtArtemisTarget(true)}
          onRotationUpdate={handleRotationUpdate}
          forceLOD={focusedLocation?.content?.type === 'launchplatform' ? (isMobile ? 'LOD_2' : 'LOD_1') : null}
          focusTargetRotation={focusedLocation?.targetEarthRotation ?? null}
          innerGroupRef={earthInnerGroupRef}
        />
        
        {currentMode === nav.modes.MAIN && !isArtemisModeActive && sceneReady && !focusedLocation && !showJoinMission && (
          <>
            <SatelliteOrbit
              radius={1.5}
              segments={128}
              rotationX={Math.PI / 3}
              rotationY={0}
              rotationZ={0}
              color="#E3E3E3"
              opacity={0.33}
              dashSize={0.025}
              gapSize={0.075}
              rotationSpeed={earthRotationSpeed}
            />
            <OrbitMarker
              orbitRadius={1.5}
              angle={-Math.PI / 0.8}
              rotationX={Math.PI / 3}
              rotationZ={0}
              dotColor="#EE3A17"
              bracketColor="#E3E3E3"
              blink={hotspotBlinkRef.current}
              text="SATELLITES"
              textPosition="right"
              textColor="#E3E3E3"
              onClick={() => { playSound('TrackVehicle'); trackEvent('hotspot_satellites'); handleFocusLocation(satelliteLocation); }}
            />
            <StaticMarker
              position={[3.4, -0.155, 3.5]}
              dotColor="#EE3A17"
              bracketColor="#E3E3E3"
              blink={spacestationHotspotBlinkRef.current}
              text="SPACE STATIONS"
              textPosition="left"
              textColor="#E3E3E3"
              onClick={() => { playSound('TrackVehicle'); trackEvent('hotspot_spacestation'); handleFocusLocation(focusedLocation ? null : gatewaySpacestation); }}
            />
            <EarthSurfaceMarker
              latitude={60.25}
              longitude={-10}
              latitudeOffset={earthCoordinateOffset.latitude}
              longitudeOffset={earthCoordinateOffset.longitude}
              earthRadius={1}
              earthInnerGroupRef={earthInnerGroupRef}
              dotColor="#EE3A17"
              bracketColor="#E3E3E3"
              blink={londonHotspotBlinkRef.current}
              text="LAUNCH PLATFORMS"
              textPosition="right"
              textColor="#E3E3E3"
              onClick={() => { playSound('TrackVehicle'); trackEvent('hotspot_launch-platforms'); handleFocusLocation(focusedLocation ? null : launchPlatform); }}
            />
          </>
        )}

        {currentMode === nav.modes.MAIN && !isArtemisModeActive && sceneReady &&
          focusedLocation?.content?.type === 'launchplatform' &&
          focusedLocation?.marker && (
          <EarthSurfaceMarker
            latitude={focusedLocation.marker.latitude}
            longitude={focusedLocation.marker.longitude}
            latitudeOffset={earthCoordinateOffset.latitude}
            longitudeOffset={earthCoordinateOffset.longitude}
            earthRadius={1}
            earthInnerGroupRef={earthInnerGroupRef}
            dotColor="#EE3A17"
            bracketColor="#E3E3E3"
            blink={londonHotspotBlinkRef.current}
            text={focusedLocation.marker.text}
            variant="line"
            onClick={() => { if (focusedLocation.marker.iframeUrl) { playSound('PlotCourse'); trackEvent(`launch-platforms_${focusedLocation.id}`); handleOpenIframe(focusedLocation.marker.iframeUrl, focusedLocation.marker.iframeLabel); } }}
          />
        )}
        
        <MoonOrbit
          earthPosition={earthPosition}
          orbitRadius={moonOrbitRadius}
          orbitSpeed={earthRotationSpeed}
          isOrbiting={false}
        >
          {(position) => (
            <group position={position}>
              <Moon
                radius={0.183}
                rotation={[0, -Math.PI/1, -1.54 * Math.PI / 180]}
                autoRotate={true}
                rotationSpeed={earthRotationSpeed}
                textureQuality="auto"
                position={[0, 0, 0]}
                onLoad={() => {}}
                preloadComplete={preloadComplete}
                isOrbiting={false}
                forceLOD={focusedLocation?.id === 'lunarops' ? (isMobile ? 'LOD_2' : 'LOD_1') : null}
              />
              {currentMode === nav.modes.MAIN && !isArtemisModeActive && sceneReady && !showJoinMission && !(focusedLocation?.id === 'lunarops' && munPanelActive) && (
                <MoonSurfaceMarker
                  latitude={lunarOpsLat}
                  longitude={lunarOpsLon}
                  moonRadius={0.183}
                  moonTilt={0}
                  dotColor="#EE3A17"
                  bracketColor="#E3E3E3"
                  blink={lunarOpsHotspotBlinkRef.current}
                  text={focusedLocation?.id === 'lunarops' ? 'EXPLORE THE MÜN' : 'LUNAR OPS'}
                  variant={focusedLocation?.id === 'lunarops' ? 'line' : 'brackets'}
                  textColor={focusedLocation?.id === 'lunarops' ? '#EE3A17' : '#E3E3E3'}
                  textPosition="left"
                   onClick={() => {
                     if (focusedLocation?.id === 'lunarops') {
                       playSound('DataPoint_Open')
                       trackEvent('lunarops_hotspot_explore-the-mun')
                       if (mediaQueryKey === 'mobilePortrait') {
                         handleOpenIframe('https://mun.darkstarlabs.io/', 'Explore the Mün')
                       } else {
                         setMunPanelActive(prev => !prev)
                         setMeridianPanelActive(false)
                       }
                     } else {
                       playSound('TrackVehicle')
                       trackEvent('hotspot_lunarops')
                       handleFocusLocation(lunarOpsLocation)
                     }
                   }}
                />
              )}
              {/* Second marker - only at Lunar Ops location, floating above surface */}
              {focusedLocation?.id === 'lunarops' && !meridianPanelActive && !showJoinMission && (
                <MoonSurfaceMarker
                  latitude={-48}
                  longitude={145}
                  moonRadius={0.23}
                  moonTilt={0}
                  dotColor="#EE3A17"
                  bracketColor="#E3E3E3"
                  blink={lunarOpsHotspotBlinkRef.current}
                  text="MERIDIAN'S ETV"
                  variant="line"
                  textColor="#EE3A17"
                   onClick={() => { playSound('DataPoint_Open'); trackEvent('lunarops_hotspot_meridians-etv'); if (mediaQueryKey === 'mobilePortrait') { handleOpenIframe('https://meridian-space-etv.vercel.app/', "Meridian's ETV") } else { setMeridianPanelActive(prev => !prev); setMunPanelActive(false) } }}
                />
              )}
              {/* Meridian model - only at Lunar Ops location */}
              {focusedLocation?.id === 'lunarops' && (
                <MeridianModel
                  position={[
                    0.23 * Math.cos(-48 * Math.PI / 180) * Math.cos(145 * Math.PI / 180),
                    0.23 * Math.sin(-48 * Math.PI / 180),
                    0.23 * Math.cos(-48 * Math.PI / 180) * Math.sin(145 * Math.PI / 180)
                  ]}
                  rotation={[0, 3, 1]}
                  scale={0.008}
                />
              )}
            </group>
          )}
        </MoonOrbit>
        
        {currentMode === nav.modes.MAIN && isArtemisModeActive && sceneReady && !showJoinMission && !showMissionObjective && (
          <StaticMarker
            position={[4.33, 0.18, 4.12]}
            dotColor="#EE3A17"
            bracketColor="#E3E3E3"
            blink={moonObjectiveHotspotBlinkRef.current}
            text="MISSION OBJECTIVE"
            textPosition="left"
            textColor="#E3E3E3"
            onClick={() => {
              playSound('TrackVehicle')
              trackEvent('artemis_mission_objective_click')
              handleOpenMissionObjective()
            }}
          />
        )}
        
        {(currentMode === nav.modes.ARTEMIS || showTrajectory) && earthAtArtemisTarget && (
          <TrajectoryEditor 
            color="#ff6b6b"
            tension={0.95}
            showGizmos={false}
            onGrowComplete={() => setTrajectoryDrawComplete(true)}
          />
        )}
        
        {(currentMode === nav.modes.ARTEMIS || showTrajectory) && earthAtArtemisTarget && artemisMissionLaunched && (
          <>
            <ArtemisOnPath
              controlPoints={artemisControlPoints}
              duration={120} // Duration in seconds for rocket to complete full trajectory (increase for slower, decrease for faster)
              visible={true}
              onProgressUpdate={setArtemisProgress}
              onComplete={() => setArtemisComplete(true)}
              trajectoryDrawComplete={trajectoryDrawComplete}
              onPositionUpdate={handleArtemisPositionUpdate}
              showBrackets={artemisMissionLaunched && artemisProgress >= 10}
              bracketBlinkRef={artemisBlinkRef}
            >
              {/* Chase camera only rendered on non-mobile devices and when follow mode is NOT active */}
              {!deviceInfo.isPhone && !followModeActive && (
                <ArtemisChaseCamera
                  onTextureUpdate={handleChaseCameraRenderTargetUpdate}
                />
              )}
            </ArtemisOnPath>
            
            
            {/* Mission stage markers on trajectory - not clickable on mobile portrait */}
            {!showJoinMission && !isDropdownExpanded && missionStagesConfig.missionStages.map((stage, index) => {
              if (!stage.visible) return null
              const reached = artemisProgress >= stage.trajectoryPercent
              const isActiveMarker = activeTooltip && activeTooltip.id === stage.id
              return (
                <MissionStageMarker
                  key={stage.id}
                  controlPoints={artemisControlPoints}
                  trajectoryPercent={stage.trajectoryPercent}
                  stageName={stage.name}
                  stageNumber={index + 1}
                  stageId={stage.id}
                  reached={reached}
                  onClick={mediaQueryKey !== 'mobilePortrait' ? handleMissionStageClick : null}
                  onPositionUpdate={handleMarkerPositionUpdate}
                  isActive={isActiveMarker}
                />
              )
            })}
            
            {/* Simple plane in 3D space displaying chase camera texture - only on non-mobile and when follow mode is NOT active */}
            {!deviceInfo.isPhone && !followModeActive && (
              <ArtemisChaseCameraView
                renderTarget={chaseCameraRenderTarget}
                isActive={artemisMissionLaunched && artemisProgress >= 8}
                artemisProgress={artemisProgress}
              />
            )}
          </>
        )}
        
        {/* Satellite models - stacked vertically, scale animated by SatelliteScaleAnimator */}
         <group position={satellitePosition}>
           {/* DslSat — index 0; starts at scale 0, scaled up by SatelliteScaleAnimator after camera zoom */}
           <group ref={satelliteModelRef} scale={[0, 0, 0]} position={[0, (satelliteModels[0]?.positionOffset?.[1] ?? -0.5), 0]}>
             <DslSatInstances>
               <DslSat scale={satelliteModels[0]?.scale ?? 0.05} />
             </DslSatInstances>
           </group>
            {/* IRUS — index 1; starts at scale 0, scaled up by SatelliteScaleAnimator after camera zoom */}
            <group ref={irusModelRef} scale={[0, 0, 0]} position={[0, (satelliteModels[1]?.positionOffset?.[1] ?? 0.5), 0]}>
              <IrusModel scale={satelliteModels[1]?.scale ?? 0.05} />
            </group>
            {/* Prospero — index 2; starts at scale 0, scaled up by SatelliteScaleAnimator after camera zoom */}
            <group ref={prosperoModelRef} scale={[0, 0, 0]} position={[0, (satelliteModels[2]?.positionOffset?.[1] ?? -1.5), 0]}>
              <Prospero scale={satelliteModels[2]?.scale ?? 0.05} />
            </group>
         </group>


        {/* Gateway model - location spin controller handles drag, inertia, spring return */}
        <group ref={locationModelRef} position={gatewayPosition}>
          <GatewayInstances visible={!isArtemisModeActive && ((currentMode === nav.modes.MAIN && !focusedLocation) || focusedLocation?.id === 'spacestation')}>
            <GatewayModel
              scale={gatewayScale}
              visible={!isArtemisModeActive && ((currentMode === nav.modes.MAIN && !focusedLocation) || focusedLocation?.id === 'spacestation')}
            />
          </GatewayInstances>
        </group>

        <LocationSpinController groupRef={locationModelRef} isFocused={focusedLocation?.id === 'spacestation'} initialRotation={gatewayRotation} />

        {/* Gateway hotspot — hidden on mobile portrait */}
        {gatewaySpacestation?.hotspot && mediaQueryKey !== 'mobilePortrait' && (() => {
          const hs = gatewaySpacestation.hotspot
          return (
            <SatelliteHotspot
              satelliteGroupRef={locationModelRef}
              offset={hs.offset ?? [0, 0, 0]}
              direction={hs.direction ?? 'right'}
              line={hs.line ?? {}}
              text={hs.text ?? gatewaySpacestation.name}
              visible={focusedLocation?.id === 'spacestation' && !gatewayHotspotActive}
              onClick={() => { playSound('DataPoint_Open'); trackEvent('spacestation_hotspot_explore-station'); setGatewayHotspotActive(true) }}
            />
          )
        })()}
        <LocationSpinController groupRef={satelliteModelRef} isFocused={focusedLocation?.id === 'satellite' && focusedSatelliteIndex === 0} initialRotation={satelliteModels[0]?.rotation ?? [0, 2.5, 0]} />
        <LocationSpinController groupRef={irusModelRef} isFocused={focusedLocation?.id === 'satellite' && focusedSatelliteIndex === 1} initialRotation={satelliteModels[1]?.rotation ?? [0, 0, 0]} />
        <LocationSpinController groupRef={prosperoModelRef} isFocused={focusedLocation?.id === 'satellite' && focusedSatelliteIndex === 2} initialRotation={satelliteModels[2]?.rotation ?? [0, 0, 0]} />
        <SatelliteScaleAnimator targetScalesRef={targetScalesRef} />

        {/* Satellite hotspots — one per model, only visible when that model is focused */}
        {satelliteModels.map((model, i) => {
          const ref = [satelliteModelRef, irusModelRef, prosperoModelRef][i]
          const isFocused = focusedLocation?.id === 'satellite' && focusedSatelliteIndex === i
          const panelOpen = activeSatHotspot === i
          return (
            <group key={model.id}>
              {/* Primary hotspot — opens info panel (hidden on mobile portrait) */}
              {model.hotspot && mediaQueryKey !== 'mobilePortrait' && (
                <SatelliteHotspot
                  satelliteGroupRef={ref}
                  offset={model.hotspot.offset ?? [0, 0, 0]}
                  direction={model.hotspot.direction ?? 'right'}
                  line={model.hotspot.line ?? {}}
                  text={model.hotspot.text ?? model.name}
                  visible={isFocused && !panelOpen}
                  onClick={() => { playSound('DataPoint_Open'); trackEvent(`satellite_hotspot_${model.hotspot.text.toLowerCase().replace(/\s+/g, '-')}`); setActiveSatHotspot(prev => prev === i ? null : i) }}
                />
              )}
              {/* Extra hotspots (e.g. URL launchers) */}
              {model.hotspots?.map((hs, j) => (
                <SatelliteHotspot
                  key={j}
                  satelliteGroupRef={ref}
                  offset={hs.offset ?? [0, 0, 0]}
                  mobileOffset={hs.mobileOffset ?? null}
                  direction={hs.direction ?? 'right'}
                  line={hs.line ?? {}}
                  text={hs.text ?? ''}
                  visible={isFocused}
                  onClick={hs.url ? () => { playSound('PlotCourse'); trackEvent(`satellite_hotspot_${hs.text.toLowerCase().replace(/\s+/g, '-')}`); handleOpenIframe(hs.url, hs.iframeLabel ?? '') } : null}
                />
              ))}
            </group>
          )
        })}
        
        {/* Location component for camera transitions - hidden in artemis mode */}
        {currentLocation && !isArtemisModeActive && (
          <Location
            locationData={currentLocation}
            isActive={true}
          />
        )}
      </WebGPUCanvas>
      
      {/* Mission completion text below chase camera - only on non-mobile and when follow mode is NOT active */}
      {artemisMissionLaunched && artemisProgress >= 8 && !deviceInfo.isPhone && !followModeActive && (
        <div className="chase-camera-mission-text">
          <div>MISSION COMPLETION</div>
          <div>/// {artemisProgress}%</div>
        </div>
      )}
      
      {/* Trajectory Editor UI - hidden */}
      {false && isDevMode && currentMode === nav.modes.ARTEMIS && earthAtArtemisTarget && <TrajectoryEditorUI />}
      
      
      {/* Mission stage tooltip - appears when marker is clicked */}
      {activeTooltip && (
        <div 
          className="mission-stage-tooltip"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -120%)'
          }}
        >
          <div className="mission-stage-tooltip-content">
            <div style={{ fontWeight: '700', marginBottom: activeTooltip.description ? '8px' : '0' }}>
              {activeTooltip.name}
            </div>
            {activeTooltip.description && (
              <div style={{ 
                fontSize: '12px', 
                fontWeight: '400', 
                opacity: '0.8',
                textTransform: 'none',
                letterSpacing: '0.5px'
              }}>
                {activeTooltip.description}
              </div>
            )}
          </div>
        </div>
      )}
      
      {showMissionObjective && !showJoinMission && (
        <DSL_SmallWindow
          onClose={handleCloseMissionObjective}
          isClosing={isClosingMissionObjective}
          isTransitioning={isTransitioning}
        >
          <h1 className="sw-heading">
            MISSION OBJECTIVE:
          </h1>
          <p className="sw-body">
            For the first time in over 50 years, four astronauts will journey beyond Earth's orbit to test the Orion spacecraft's life-support and manual piloting systems.
          </p>
          <p className="sw-body sw-body--last">
            This 10-day mission will carry the crew roughly <span className="sw-highlight">10,300 km (6,400 miles)</span> past the far side of the Moon, setting a new record for the farthest distance humans have ever travelled from Earth.
          </p>
        </DSL_SmallWindow>
      )}
      
      {showMissionComplete && !showJoinMission && (
        <DSL_SmallWindow
          onClose={handleCloseMissionComplete}
          isClosing={isClosingMissionComplete}
          isTransitioning={isTransitioning}
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
        >
          <h1 className="sw-heading">
            MISSION COMPLETE
          </h1>
          <p className="sw-body sw-body--center sw-body--last">
            Follow us for more interactive missions
          </p>
          <div className="sw-qr-container">
            {mediaQueryKey === 'mobilePortrait' ? (
              <a
                href="https://www.linkedin.com/company/darkstarlabs/"
                target="_blank"
                rel="noopener noreferrer"
                className="sw-linkedin-link"
              >
                <FaLinkedin size={80} color="#EE3A17" />
              </a>
            ) : (
              <QRCodeSVG
                value="https://www.linkedin.com/company/darkstarlabs/"
                size={is4K ? 260 : 180}
                level="H"
                fgColor="#EE3A17"
                bgColor="transparent"
              />
            )}
          </div>
        </DSL_SmallWindow>
      )}
      
    </div>
  )
}

export default function App() {
  return (
    <ArtemisModeProvider>
      <PasswordGate>
        <LocationProvider>
          <SceneContent />
        </LocationProvider>
      </PasswordGate>
    </ArtemisModeProvider>
  )
}
